// libs
import cx from "classnames";
import React, { ReactElement, useCallback, useState, useRef, RefObject, useEffect } from "react";

// components
import { BottomSheet, BottomSheetChildrenProps } from "@swiggy-private/core-ui";

// widgets
import { Button, BUTTON_ACCENT } from "@payment-ui/widgets/button";

// utils
import { useFocusTrap } from "@swiggy-private/core-ui";

//constants
import { ACCESSIBILITY_ROLES } from "@payment-ui/constants/index";
import { PAYMENT_ALERT_HEADER_TITLE_ID, PAYMENT_ALERT_CONTENT_ID } from "@payment-ui/constants/PaymentAlert";

//helpers
import { getCardBrandFormattedString } from "@payment-ui/helpers/Card";

// test-ids
import { PAYMENT_ALERT_TEST_IDS } from "@swiggy-private/payment-test-ids";

//hooks
import { useFocusDialog, getCloudinaryUrl, getActiveElement } from "@swiggy-private/core-ui";

// styles
import styles from "./DeleteCardPrompt.scss";

export interface DeleteCardActionProps {
    text: string;
    onClick: () => void;
}

export interface DeleteCardConfirmProps {
    title: string;
    message?: string;
    iconUrl: string;
    primaryAction?: DeleteCardActionProps;
    hideOnOverlayClick?: boolean;
    onClose?: VoidFunction;
    cardBrand?: string;
}

type deleteCardConfirmContentProps = Omit<DeleteCardConfirmProps, "hideOnOverlayClick" | "onClose"> & {
    setAction: (fn: VoidFunction) => void;
};

const DeleteCardConfirmContent = React.memo(function DeleteCardConfirmContents(
    props: deleteCardConfirmContentProps & BottomSheetChildrenProps,
): React.ReactElement {
    const headerDivRef = useRef<HTMLElement | null>(null);
    const previouslyFocusedElement = useRef<HTMLElement | null>(null);
    useEffect(() => {
        previouslyFocusedElement.current = getActiveElement();
    }, []);

    useFocusDialog(headerDivRef, previouslyFocusedElement);

    const handlePrimaryActionClick = (): void => {
        if (props.primaryAction) {
            props.setAction(props.primaryAction.onClick);
            props.close();
        }
    };

    const actionClass = cx(styles.deleteCardAction, {
        [styles.deleteCardActionSingle]: true,
    });

    const ImageURL = getCloudinaryUrl(props.iconUrl);

    return (
        <div
            role={ACCESSIBILITY_ROLES.ALERTDIALOG}
            aria-labelledby={PAYMENT_ALERT_HEADER_TITLE_ID}
            aria-describedby={PAYMENT_ALERT_CONTENT_ID}
        >
            <div className={styles.deleteCardHeader} data-testid={PAYMENT_ALERT_TEST_IDS.HEADER}>
                <div className={styles.deleteCardHeaderInfo}>
                    <div className={styles.deleteCardHeaderIconContainer}>
                        <img className={styles.deleteCardHeaderIcon} src={ImageURL} alt="" />
                    </div>
                    <div className={styles.deleteCardHeaderConfirmContainer}>
                        <div
                            id={PAYMENT_ALERT_HEADER_TITLE_ID}
                            className={styles.deleteCardHeaderTitle}
                            data-testid={PAYMENT_ALERT_TEST_IDS.HEADER_TITLE}
                            ref={headerDivRef as RefObject<HTMLDivElement>}
                            tabIndex={0}
                            role={ACCESSIBILITY_ROLES.HEADING}
                            aria-level={3}
                        >
                            {getCardBrandFormattedString(props.title, props.cardBrand ? props.cardBrand : "")}
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.deleteCardContent} data-testid={PAYMENT_ALERT_TEST_IDS.CONTENT}>
                {props.primaryAction && (
                    <Button
                        onClick={handlePrimaryActionClick}
                        accent={BUTTON_ACCENT.SWIGGY_ORANGE_TRANSPARENT}
                        className={actionClass}
                        data-testid={PAYMENT_ALERT_TEST_IDS.PRIMARY_ACTION_BUTTON}
                    >
                        <div className={styles.deleteCardActionMain}>{props.primaryAction.text}</div>
                    </Button>
                )}
            </div>
        </div>
    );
});

function DeleteCardConfirmPrompt(props: DeleteCardConfirmProps): ReactElement {
    const { hideOnOverlayClick = true, onClose, ..._props } = props;
    const [actionOnClose, setActionOnClose] = useState<VoidFunction | undefined>();
    const _onClose = useCallback((): void => {
        actionOnClose && actionOnClose();
        onClose && onClose();
    }, [onClose, actionOnClose]);

    useFocusTrap();
    return (
        <BottomSheet hideOnOverlayClick={hideOnOverlayClick} onClose={_onClose}>
            {(bProps: BottomSheetChildrenProps): React.ReactElement => (
                <DeleteCardConfirmContent {...bProps} {..._props} setAction={setActionOnClose} />
            )}
        </BottomSheet>
    );
}

export default React.memo(DeleteCardConfirmPrompt);
